/**
 * Date configuration for various locales.
 *
 * Contains
 *  - Order for order of year, month, day (YMD) etc.
 *  - Format for formatting date with tools such as Luxon.
 *  - Placeholder to display as a hint for date input (translated).
 *
 * It is important that the three values are somewhat in sync, the order must
 * be the same, and the format and placeholder must match except for the
 * delimiter.
 */
export const dateLocalizationConfig: Record<
  string,
  Record<string, { order: 'YMD' | 'DMY' | 'MDY'; format: string; placeholder: string }>
> = {
  AT: {
    de: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'TT-MM-JJJJ',
    },
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
  },
  AU: {
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
  },
  BE: {
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
    fr: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'JJ-MM-AAAA',
    },
    nl: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-JJJJ',
    },
  },
  CA: {
    en: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'YYYY-MM-DD',
    },
    fr: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'AAAA-MM-JJ',
    },
  },
  CH: {
    de: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'TT.MM.JJJJ',
    },
    en: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.YYYY',
    },
    fr: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'JJ.MM.AAAA',
    },
    it: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'GG.MM.AAAA',
    },
  },
  CZ: {
    cs: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.RRRR',
    },
    en: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.YYYY',
    },
  },
  DE: {
    de: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'TT.MM.JJJJ',
    },
    en: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.YYYY',
    },
  },
  DK: {
    da: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-ÅÅÅÅ',
    },
    en: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.YYYY',
    },
  },
  ES: {
    es: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-AAAA',
    },
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
    ca: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-AAAA',
    },
    eu: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'EE-HH-UUUU',
    },
    gl: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'EE-HH-UUUU',
    },
  },
  FI: {
    en: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.YYYY',
    },
    fi: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'PP-KK-VVVV',
    },
  },
  FR: {
    en: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'YYYY-MM-DD',
    },
    fr: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'AAAA-MM-JJ',
    },
  },
  GB: {
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
  },
  HR: {
    en: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.YYYY',
    },
    hr: {
      order: 'DMY',
      format: 'dd.LL.yyyy.',
      placeholder: 'DD.MM.GGGG.',
    },
  },
  HU: {
    en: {
      order: 'YMD',
      format: 'yyyy.LL.dd',
      placeholder: 'YYYY.MM.DD',
    },
    hu: {
      order: 'YMD',
      format: 'yyyy.LL.dd.',
      placeholder: 'ÉÉÉÉ.HH.NN.',
    },
  },
  IE: {
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
  },
  IN: {
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
  },
  IT: {
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
    it: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'GG-MM-AAAA',
    },
  },
  JP: {
    en: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'YYYY-MM-DD',
    },
    ja: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'YYYY-MM-DD',
    },
  },
  KR: {
    en: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'YYYY-MM-DD',
    },
    ko: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'YYYY-MM-DD',
    },
  },
  NL: {
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
    nl: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-JJJJ',
    },
  },
  NO: {
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
    no: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-ÅÅÅÅ',
    },
  },
  PL: {
    en: {
      order: 'DMY',
      format: 'dd/LL/yyyy',
      placeholder: 'DD/MM/YYYY',
    },
    pl: {
      order: 'DMY',
      format: 'dd/LL/yyyy',
      placeholder: 'DD/MM/RRRR',
    },
  },
  PT: {
    pt: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-AAAA',
    },
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
  },
  RO: {
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
    ro: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'ZZ-LL-AAAA',
    },
  },
  RS: {
    en: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-YYYY',
    },
    sr: {
      order: 'DMY',
      format: 'dd-LL-yyyy',
      placeholder: 'DD-MM-GGGG',
    },
  },
  SE: {
    en: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'YYYY-MM-DD',
    },
    sv: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'ÅÅÅÅ-MM-DD',
    },
  },
  SI: {
    en: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.YYYY',
    },
    sl: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.LLLL',
    },
  },
  SK: {
    en: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.YYYY',
    },
    sk: {
      order: 'DMY',
      format: 'dd.LL.yyyy',
      placeholder: 'DD.MM.RRRR',
    },
  },
  US: {
    en: {
      order: 'MDY',
      format: 'LL-dd-yyyy',
      placeholder: 'MM-DD-YYYY',
    },
  },
  XZ: {
    en: {
      order: 'YMD',
      format: 'yyyy-LL-dd',
      placeholder: 'YYYY-MM-DD',
    },
  },
};
